import PropTypes from 'prop-types';

/**
 * 共用影片卡片組件
 *
 * @param {Object} video - 影片資料
 * @param {Function} onVideoClick - 點擊影片的回調函數
 * @param {string} className - 額外的 CSS class
 */
const VideoCard = ({
    video,
    onVideoClick,
    className = '',
}) => {
    const handleClick = () => {
        if (onVideoClick) {
            onVideoClick(video);
        }
    };

    return (
        <div className={`group ${className}`}>
            <div
                className="relative aspect-video rounded-[33px] overflow-hidden shadow-[0px_2px_10px_0px_rgba(0,0,0,0.35)] border border-[var(--color-gold)]/20 hover:shadow-xl hover:border-[var(--color-gold)]/40 transition-all cursor-pointer"
                onClick={handleClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleClick()}
            >
                {video.thumbnail ? (
                    <img
                        alt={video.title}
                        className="w-full h-full object-cover"
                        src={video.thumbnail}
                        onError={(e) => {
                            e.target.src = "https://via.placeholder.com/640x360/c4a25f/ffffff?text=Video+Thumbnail";
                        }}
                    />
                ) : video.videoUrl ? (
                    // 沒有 thumbnail 時，用 <video> 靜止顯示第一幀（不需要 CORS）
                    <video
                        src={`${video.videoUrl}#t=0.1`}
                        className="w-full h-full object-cover"
                        preload="metadata"
                        muted
                        playsInline
                        controlsList="nodownload"
                        onContextMenu={(e) => e.preventDefault()}
                    />
                ) : (
                    <div className="w-full h-full bg-black/80 flex items-center justify-center">
                        <span className="material-symbols-outlined text-white/30 text-6xl">movie</span>
                    </div>
                )}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                    <span className="material-symbols-outlined text-white text-5xl md:text-6xl lg:text-7xl opacity-90 group-hover:scale-110 transition-transform">
                        play_circle
                    </span>
                </div>

                {/* 觀看次數標籤 */}
                {video.views && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 md:px-3 md:py-1 bg-black/60 backdrop-blur-sm rounded text-[10px] md:text-xs text-white/90 font-medium flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs">visibility</span>
                        {video.views}
                    </div>
                )}
            </div>

            {/* 影片資訊 */}
            <div className="mt-3 md:mt-4 px-1 text-center">
                <p className="text-sm md:text-base font-medium text-[var(--color-text-main)] line-clamp-2 leading-snug mb-1">
                    {video.title}
                </p>
                {video.description && (
                    <p className="text-xs md:text-sm text-[var(--color-text-sub)] line-clamp-2 leading-relaxed">
                        {video.description}
                    </p>
                )}
            </div>
        </div>
    );
};

VideoCard.propTypes = {
    video: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        videoUrl: PropTypes.string,
        thumbnail: PropTypes.string,
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        duration: PropTypes.string,
        views: PropTypes.string,
    }).isRequired,
    onVideoClick: PropTypes.func,
    className: PropTypes.string,
};

export default VideoCard;
